import { useLazyQuery } from '@apollo/client'
import gql from 'graphql-tag'
import { useCallback } from 'react'

// Work Order type based on the expected response
export type WorkOrder = {
	work_order_id: number,
}

export type WorkOrderPayload = {
	workorders: WorkOrder[],
}

type Variables = {
	search?: string,
	branchId?: string,
}

export const buildPath = ( { args }: { args: Variables } ) => {
	let path = `/work-orders?work_order_status_id=1&size=10&work_order_type_id=1&search=${encodeURIComponent( args.search ?? '' )}`
	if ( args.branchId ) {
		path += `&branch_id=${encodeURIComponent( args.branchId )}`
	}
	return path
}

// GraphQL query using Apollo Link REST pattern
export const workOrdersQuery = gql`
	query WorkOrderQuery($search: String, $buildPath: any!, $branchId: String) {
		workorders(search: $search, branchId: $branchId)
			@rest(
				type: "WorkOrderPayload"
				pathBuilder: $buildPath
				endpoint: "esapi"
			) {
			work_order_id
		}
	}
`

export type UseWorkOrdersQueryResult = {
	searchWorkOrders: ( search: string, branchId?: string ) => void,
	workOrders: WorkOrder[],
	loading: boolean,
	error: any,
}

/**
 * Hook for querying work orders using Apollo Link REST
 * Provides search functionality with optional branch ID filtering
 */
export default function useWorkOrdersQuery(): UseWorkOrdersQueryResult {
	const [ executeQuery, { data, loading, error } ] = useLazyQuery<WorkOrderPayload>( workOrdersQuery, {
		errorPolicy: 'all',
		fetchPolicy: 'cache-and-network',
	} )

	const searchWorkOrders = useCallback( ( search: string, branchId?: string ) => {
		if ( !search ) {
			return
		}

		executeQuery( {
			variables: {
				buildPath,
				search,
				branchId: branchId ?? undefined,
			},
		} )
	}, [ executeQuery ] )

	return {
		searchWorkOrders,
		workOrders: data?.workorders ?? [],
		loading,
		error,
	}
}
